<template>
	<AppLayout :title="trans('menu.association.representatives.admissions')">
		<Modal id="create-admission-modal">
			<template #title>{{ trans('rotations.admissions.create.title') }}</template>
			<div class="grid grid-cols-2 gap-3">
				<LabeledObject full>
					<template #label>{{ trans('rotations.admissions.starts_at') }}</template>
					<IconDateTimeInput v-model="form.starts_at" icon-class="fa-solid fa-hourglass-start" full/>
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('rotations.admissions.ends_at') }}</template>
					<IconDateTimeInput v-model="form.ends_at" icon-class="fa-solid fa-hourglass-end" full/>
				</LabeledObject>
			</div>
			<Card v-for="rotation in admissibleRotations" :key="`rotation-${rotation.id}`" :title="rotation.college.name" @click.stop="toggleRotation(rotation.id)">
				<i v-if="form.rotations.includes(rotation.id)" class="fa-solid fa-toggle-on text-green-600"/>
				<i v-else class="fa-solid fa-toggle-off text-red-600"/>
			</Card>
			<template #footer>
				<PrimaryButton v-if="form.rotations.length > 0" :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton v-if="canCreateAdmissions && admissibleRotations.length > 0" :color="Color.Green" @click.stop="showModal('create-admission-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Empty v-if="admissions.length === 0"/>
		<CardGrid v-else>
			<Card v-for="admission in admissions" :key="`admission-election-${admission.id}`" :title="`${dayjs(admission.starts_at).format('DD MMM YY HH:mm')} - ${dayjs(admission.ends_at).format('DD MMM YY HH:mm')}`" :subtitle="transChoice('rotations.admissions.rotations_count', admission.rotations_count)">
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('association.representatives.admissions.show', admission.id)">
						<i class="fa-solid fa-eye mr-2"/>
						{{ trans('general.view') }}
					</Link>
					<Link v-if="admission.open && canCastVote" :as="SecondaryButton" :color="Color.Purple" :href="route('association.representatives.admissions.vote.index', admission.id)">
						<i class="fa-solid fa-vote-yea mr-2"/>
						{{ trans('rotations.admissions.vote.title') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import Modal from '@/Components/UI/Modals/Modal.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type InertiaForm, Link, useForm} from '@inertiajs/vue3'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {type AdmissionElection} from '@/Types/Elections/AdmissionElection'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	canCastVote:boolean,
	canCreateAdmissions:boolean,
	admissions:Array<AdmissionElection>,
	admissibleRotations:Array<Rotation>,
}>()

const working:Ref<boolean> = ref(false)
const admissions:Ref<Array<AdmissionElection>> = ref(props.admissions)
const form:InertiaForm<{starts_at:string, ends_at:string, rotations:Array<number>}> = useForm<{starts_at:string, ends_at:string, rotations:Array<number>}>({
	starts_at: '',
	ends_at: '',
	rotations: [],
})

function toggleRotation(id:number):void{
	const index:number = form.rotations.findIndex((selectedRotationId:number) => selectedRotationId === id)
	if(index > -1){
		form.rotations.splice(index, 1)
	} else {
		form.rotations.push(id)
	}
}

function store():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.inserting'))
		form.post(route('association.representatives.admissions.store'), {
			onSuccess: () => {
				showSuccess(trans('general.inserted'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				working.value = false
			},
		})
	}
}
</script>